import React from 'react';
import {formatDurationCompact} from '../../lib/audioMetadata';
import {formatExactDate, formatRelativeDate} from '../../lib/formatDate';
import {getEnvironment} from '../../lib/api';
import {AlertTriangleIcon, ExternalLinkIcon} from '../Icons';

/**
 * The furniture the admin screens share: panels, key/value rows, paging, the empty and error
 * states, and the two formatters every screen needs (a duration in seconds, and a timestamp that
 * has to read as both "when" and "exactly when").
 *
 * These are view-only — no store access, no fetching. Containers pass everything in.
 */

/* ==========================================================================
 * Formatting
 * ========================================================================== */

/** Seconds → `3h 42m`. Null renders as an em dash, never as `0s`: unknown is not zero. */
export const formatSeconds = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) {
    return '—';
  }
  if (seconds === 0) {
    return '0m';
  }
  return formatDurationCompact(seconds);
};

/**
 * A count that should always be a number but, on the admin endpoints, sometimes isn't yet — these
 * are new fields and not every one has landed on every response. Renders an em dash rather than
 * throwing on `undefined.toLocaleString()`; unknown is not zero here either.
 */
export const formatCount = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : value.toLocaleString();

/**
 * `formatRelativeDate` only looks backwards — it subtracts from now, so anything in the future
 * lands in its "under a minute" branch and reads **"Just now"**. Half the timestamps on these
 * screens are future-dated (a subscription expiry, the end of a billing period, a signup token's
 * deadline), and "Just now" against an expiry is not a cosmetic problem: it is the opposite of what
 * the field says. So future values are formatted here and only the past is delegated.
 */
const formatWhen = (iso: string): string => {
  const then = new Date(iso).getTime();

  if (Number.isNaN(then)) {
    return '—';
  }

  const msAhead = then - Date.now();

  if (msAhead <= 0) {
    return formatRelativeDate(iso);
  }

  const minutes = Math.round(msAhead / 60_000);
  if (minutes < 60) {
    return `in ${Math.max(1, minutes)} min`;
  }

  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  }

  const days = Math.round(hours / 24);
  if (days < 8) {
    return `in ${days} days`;
  }

  const date = new Date(then);
  const sameYear = date.getFullYear() === new Date().getFullYear();

  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : {year: 'numeric'}),
  });
};

/** Relative for scanning, exact on hover — every timestamp on these screens is UTC ISO 8601. */
export const Timestamp: React.FC<{iso: string | null | undefined; fallback?: string}> = ({
  iso,
  fallback = '—',
}) => {
  if (!iso) {
    return <span className="text-gray-600">{fallback}</span>;
  }

  return (
    <span title={formatExactDate(iso)} className="whitespace-nowrap">
      {formatWhen(iso)}
    </span>
  );
};

/** The billing period needs both ends spelled out — it is not the calendar month. */
export const formatPeriod = (startIso: string, endIso: string): string =>
  `${formatExactDate(startIso)} → ${formatExactDate(endIso)}`;

/* ==========================================================================
 * Layout
 * ========================================================================== */

interface PanelProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Panels wrapping their own scrolling table pass `false` so the header stays put. */
  padded?: boolean;
}

export const Panel: React.FC<PanelProps> = ({
  title,
  subtitle,
  actions,
  children,
  className = '',
  padded = true,
}) => (
  <section className={`overflow-hidden rounded-xl border border-gray-800 bg-gray-900/60 ${className}`}>
    {(title || actions) && (
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-800 px-4 py-3">
        <div className="min-w-0">
          {title && <h2 className="text-sm font-semibold text-gray-100">{title}</h2>}
          {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </header>
    )}
    <div className={padded ? 'px-4 py-3' : ''}>{children}</div>
  </section>
);

/** One key/value line. `mono` for ids, which are for copying rather than reading. */
export const Field: React.FC<{
  label: string;
  children: React.ReactNode;
  mono?: boolean;
  hint?: string;
}> = ({label, children, mono, hint}) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <dt className="shrink-0 text-xs text-gray-500" title={hint}>
      {label}
    </dt>
    <dd
      className={`min-w-0 truncate text-right text-xs text-gray-200 ${
        mono ? 'font-mono text-[11px] tabular-nums' : ''
      }`}
    >
      {children}
    </dd>
  </div>
);

export const FieldList: React.FC<{children: React.ReactNode}> = ({children}) => (
  <dl className="divide-y divide-gray-800/60">{children}</dl>
);

/* ==========================================================================
 * States
 * ========================================================================== */

export const EmptyState: React.FC<{message: string; hint?: string}> = ({message, hint}) => (
  <div className="px-4 py-10 text-center">
    <p className="text-sm text-gray-400">{message}</p>
    {hint && <p className="mt-1 text-xs text-gray-600">{hint}</p>}
  </div>
);

export const LoadingState: React.FC<{message?: string}> = ({message = 'Loading…'}) => (
  <div className="px-4 py-10 text-center text-sm text-gray-500">{message}</div>
);

export const ErrorState: React.FC<{message: string; onRetry?: () => void}> = ({message, onRetry}) => (
  <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
    <AlertTriangleIcon className="h-4 w-4 text-red-400" />
    <p className="text-sm text-gray-300">{message}</p>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="text-xs text-indigo-400 hover:text-indigo-300 cursor-pointer"
      >
        Try again
      </button>
    )}
  </div>
);

/* ==========================================================================
 * Paging
 * ========================================================================== */

interface PaginationProps {
  total: number;
  limit: number;
  offset: number;
  onOffsetChange: (offset: number) => void;
  /** What is being counted, for the "1–50 of 214 users" line. */
  noun: string;
  isLoading?: boolean;
}

/**
 * Offset paging against an exact `total` — every admin list endpoint returns one, so the range can
 * be stated rather than guessed at with a "load more" that never tells you how much is left.
 */
export const Pagination: React.FC<PaginationProps> = ({
  total,
  limit,
  offset,
  onOffsetChange,
  noun,
  isLoading,
}) => {
  const safeLimit = limit > 0 ? limit : 1;
  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + safeLimit, total);
  const canGoBack = offset > 0;
  const canGoForward = offset + safeLimit < total;

  if (total <= safeLimit && offset === 0) {
    return (
      <p className="px-4 py-2 text-[11px] text-gray-600">
        {total} {noun}
      </p>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3 border-t border-gray-800 px-4 py-2">
      <p className="text-[11px] tabular-nums text-gray-500">
        {first}–{last} of {total} {noun}
      </p>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          disabled={!canGoBack || isLoading}
          onClick={() => onOffsetChange(Math.max(0, offset - safeLimit))}
          className={paginationButtonClass}
        >
          Previous
        </button>
        <button
          type="button"
          disabled={!canGoForward || isLoading}
          onClick={() => onOffsetChange(offset + safeLimit)}
          className={paginationButtonClass}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const paginationButtonClass =
  'rounded-lg border border-gray-700 px-2.5 py-1 text-[11px] font-medium text-gray-200 '
  + 'hover:border-gray-600 hover:bg-gray-800 transition-colors disabled:opacity-30 '
  + 'disabled:cursor-not-allowed cursor-pointer';

/* ==========================================================================
 * Paddle
 * ========================================================================== */

/**
 * Billing is read-only in this panel by design — a Paddle reconcile cron would revert any manual
 * edit inside five minutes — so anything that changes a subscription has to happen in Paddle. These
 * links are the whole of that story. Sandbox vs live follows the app environment, the same
 * derivation `lib/paddle.ts` uses, so an admin on staging never lands on a live customer record.
 */
const paddleDashboardOrigin = (): string =>
  getEnvironment() === 'production'
    ? 'https://vendors.paddle.com'
    : 'https://sandbox-vendors.paddle.com';

export const PaddleLink: React.FC<{
  kind: 'customer' | 'subscription';
  id: string | null;
  label?: string;
}> = ({kind, id, label}) => {
  if (!id) {
    return <span className="text-gray-600">—</span>;
  }

  const path = kind === 'customer' ? 'customers-v2' : 'subscriptions-v2';

  return (
    <a
      href={`${paddleDashboardOrigin()}/${path}/${encodeURIComponent(id)}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 font-mono text-[11px] text-indigo-400 hover:text-indigo-300"
      title={`Open in Paddle (${getEnvironment() === 'production' ? 'live' : 'sandbox'})`}
    >
      <span className="truncate">{label ?? id}</span>
      <ExternalLinkIcon className="h-3 w-3 shrink-0" />
    </a>
  );
};
